import { UserStatus } from "@excel-center/shared";
import { Spinner } from "@heroui/react";
import { type ReactNode, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Outlet, useLocation } from "react-router";
import { AUTH_ROUTES } from "@/src/auth/constants/auth-routes.constants";
import { useAuth } from "@/src/auth/hooks/use-auth";
import { logout } from "@/src/auth/store/auth.thunks";
import type { UserRole } from "@/src/auth/types/auth.types";
import { useAppDispatch } from "@/src/common/redux/store";
import { toastService } from "@/src/common/services/toast.service";

interface ProtectedRouteProps {
	requiredRole?: UserRole | UserRole[];
	children?: ReactNode;
}

/**
 * Route guard for authed sections of the app.
 *
 * Redirect rules:
 * - Not authenticated            -> /login
 * - Profile status rejected/revoked -> sign out + /login
 * - requiredRole set and mismatched -> /
 *
 * Assumes `initializeAuth` has already been dispatched by `src/auth/index.ts`;
 * renders a spinner while `isInitialized=false`.
 */
export function ProtectedRoute({ requiredRole, children }: ProtectedRouteProps) {
	const location = useLocation();
	const { t } = useTranslation("auth");
	const dispatch = useAppDispatch();
	const { session, role, status, isInitialized } = useAuth();

	const shouldSignOut = Boolean(session && (status === UserStatus.REJECTED || status === UserStatus.REVOKED));

	useEffect(() => {
		if (!shouldSignOut) return;
		toastService.error(t("protected.rejected_toast"));
		void dispatch(logout());
	}, [shouldSignOut, dispatch, t]);

	if (!isInitialized) {
		return (
			<div className="flex min-h-[50vh] items-center justify-center">
				<Spinner />
			</div>
		);
	}

	if (!session) {
		return <Navigate to={AUTH_ROUTES.login} replace state={{ from: location.pathname }} />;
	}

	if (shouldSignOut) {
		return <Navigate to={AUTH_ROUTES.login} replace />;
	}

	if (requiredRole) {
		const allowed = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
		if (!role || !allowed.includes(role)) {
			return <Navigate to="/" replace />;
		}
	}

	return <>{children ?? <Outlet />}</>;
}
